import {
  AlertDialogContainer,
  Box,
  Button,
  FileInfo,
  PublishPopup,
  useDisclosure,
} from '@ouster/webviz-react-components';

import React from 'react';

import useChakraHook from '../../hooks/useChakraHook';
import { useStoreActions, useStoreState } from '../../reducers';

interface NavPublishProps {
  revealed: boolean;
  sourceFilesInfo: FileInfo[];
  onClickedOutside: () => void;
}

export default function NavPublish({
  revealed,
  onClickedOutside,
  sourceFilesInfo,
}: NavPublishProps) {
  const { isPublished, publicationInfo, driveInfo, isSample } = useStoreState(
    (storeState) => ({
      ...storeState.drive,
    })
  );
  const { publishDrive, updateDrivePublicTitle, unpublishDrive, setIsSample } =
    useStoreActions((actions) => actions.drive);

  const { curUserOrg, appUser } = useStoreState(
    (storeState) => storeState.user
  );

  const { showToast } = useChakraHook();

  const {
    isOpen: isAlertOpen,
    onClose: onAlertClose,
    onOpen: onAlertOpen,
  } = useDisclosure();

  const dataFilesName = sourceFilesInfo
    .filter((fileInfo) => fileInfo.type === 'data')
    .map((fileInfo) => fileInfo.filename)
    .join(' | ');

  const defaultLinkTitle = () => {
    if (!driveInfo) {
      return '';
    }

    /*
      When it is single sensor recording, we return the same title info, but for multi-sensor, we hide sensor info and just point it is multi-sensor
    */
    const isSingleSensor = driveInfo.sensorInfos.length === 1;
    const recordingTitle = driveInfo.recordingTitle
      ? `${driveInfo.recordingTitle} | `
      : '';

    if (isSingleSensor) {
      return `${recordingTitle}${driveInfo.sensorInfos[0].sdkProductType} ${
        driveInfo.sensorInfos[0].sdkProductResolution || ''
      } | ${driveInfo.sensorInfos[0].sdkFwVersionCore || ''} ${
        driveInfo.sensorInfos[0].sdkFwVersionPrerelease || ''
      }`;
    }

    return `${recordingTitle}Multi-sensor`;
  };

  /* 
    We need to check whether the recording is part of sample data or not
    If it is, we need to show user warning before we allow them to unpublish
  */
  const confirmUnpublish = async (orgId: number) => {
    try {
      await unpublishDrive(orgId);
    } catch (error) {
      showToast({
        title: 'Failed to revoke public access',
        description: `${error}`,
        status: 'error',
      });
    }
  };

  const handleUnpublish = () => {
    if (!curUserOrg?.id) return;
    if (isSample) {
      onAlertOpen();
      return;
    }
    try {
      confirmUnpublish(curUserOrg.id);
    } catch (error) {
      console.error('Error unpublishing: ', error);
    }
  };

  const handleConfirmSampleRemoval = () => {
    if (!curUserOrg?.id) return;
    setIsSample(false);
    onAlertClose();
    if (curUserOrg?.id) confirmUnpublish(curUserOrg.id);
  };

  const handlePublish = async () => {
    if (!curUserOrg?.id || !driveInfo?.id || isPublished) return;
    try {
      await publishDrive({
        title: defaultLinkTitle(),
        orgId: curUserOrg.id,
        driveId: Number(driveInfo?.id),
      });
    } catch (error) {
      showToast({
        title: 'Failed to generate public access',
        description: `${error}`,
        status: 'error',
      });
    }
  };

  const handleUpdatePublicTitle = async (title: string) => {
    if (!curUserOrg?.id) return;
    try {
      await updateDrivePublicTitle({ orgId: curUserOrg.id, title });
    } catch (error) {
      showToast({
        title: 'Failed to update public title',
        description: `${error}`,
        status: 'error',
      });
    }
  };

  const handleOpenPublicLink = () => {
    if (!publicationInfo?.linkUrl) return;
    window.open(publicationInfo.linkUrl, '_blank');
  };

  const shouldDisplayRevokeButton =
    appUser?.id === publicationInfo?.publishedById ||
    curUserOrg?.role === 'ADMIN';

  return (
    <Box zIndex={1000} data-testid="nav-publish-box">
      <PublishPopup
        revealed={revealed}
        isPublished={isPublished}
        orgName={curUserOrg?.name || ''}
        dataFilesName={dataFilesName}
        onClickedOutside={onClickedOutside}
        onPublish={handlePublish}
        onUpdateTitle={handleUpdatePublicTitle}
        publicationInfo={publicationInfo}
        onUnpublish={handleUnpublish}
        shouldDisplayRevokeButton={shouldDisplayRevokeButton}
        onOpenPublicLink={handleOpenPublicLink}
        data-testid="nav-publish-popup"
        isSample={isSample}
      />

      <AlertDialogContainer
        isOpen={isAlertOpen}
        onClose={onAlertClose}
        header="Unpublish the recording?"
        content={
          <p style={{ lineHeight: '1.6rem' }}>
            This recording has been included in sample data. Unpublish it will
            remove it from sample data page. Are you sure you want to do that.
          </p>
        }
        confirmButton={
          <Button
            onClick={handleConfirmSampleRemoval}
            variant="outline"
            colorScheme="red"
            ml={3}
            data-testid="nav-publish-confirm-btn"
          >
            Confirm
          </Button>
        }
        data-testid="nav-publish-alert"
      />
    </Box>
  );
}
